/**
 * Language toggle (EN / RU / PT / ES). Updates both the UI locale (i18next) and the feed language.
 * For authenticated users the change is sent to the server optimistically and reverted on error.
 * For guests the change is saved in feed.store only.
 */
import { accountsApi } from '@/api'
import { useAuthStore } from '@/store/auth.store'
import { useFeedStore } from '@/store/feed.store'
import type { AccountPrivate, Language } from '@olimhub/shared'

const FEED_LANGS: Language[] = ['en', 'ru', 'pt', 'es']

// dropUp kept for API compat (sidebar uses it) but no longer needed
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function LanguageSelector({ dropUp: _ = false }: { dropUp?: boolean }) {
  const { account, updateAccount, isAuthenticated } = useAuthStore()
  const { guestFeedLanguages, setGuestFeedLanguages } = useFeedStore()

  const feedLanguages: Language[] = isAuthenticated && account
    ? (account as AccountPrivate).feedLanguages ?? []
    : guestFeedLanguages

  function toggle(lang: Language) {
    const newLangs = feedLanguages.includes(lang)
      ? feedLanguages.filter(l => l !== lang)
      : [...feedLanguages, lang]

    if (isAuthenticated && account) {
      const acc = account as AccountPrivate
      updateAccount({ ...acc, feedLanguages: newLangs } as AccountPrivate)
      void accountsApi.updateMe({ displayName: acc.displayName, feedLanguages: newLangs })
        .then(updated => updateAccount(updated))
        .catch(() => updateAccount({ ...acc, feedLanguages: acc.feedLanguages ?? [] } as AccountPrivate))
    } else {
      setGuestFeedLanguages(newLangs)
    }
  }

  return (
    <div
      role="group"
      aria-label="Feed language filter"
      style={{
        display: 'inline-flex',
        padding: 3,
        gap: 2,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-pill)',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      {FEED_LANGS.map(lang => {
        const active = feedLanguages.includes(lang)
        return (
          <button
            key={lang}
            onClick={() => toggle(lang)}
            aria-pressed={active}
            style={{
              border: 'none',
              cursor: 'pointer',
              padding: '4px 10px',
              borderRadius: 'var(--radius-pill)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-xs)',
              letterSpacing: '0.02em',
              background: active ? 'var(--brand)' : 'transparent',
              color: active ? 'var(--on-brand)' : 'var(--text-muted)',
              transition: 'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)',
            }}
          >
            {lang.toUpperCase()}
          </button>
        )
      })}
    </div>
  )
}
